import { Alert, Box, Chip, Divider, LinearProgress, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Button, Card, CardDescription, CardHeader, CardTitle } from "randmarcomps";
import { inject, observer } from "mobx-react";
import * as React from "react";
import { FormattedNumber } from "react-intl";
import { Link as RouterLink, useParams } from "react-router-dom";
import GeneralDocumentSummary from "../components/document/GeneralDocumentSummary";
import CurrencyUtils from "../components/util/CurrencyUtils";
import { DocumentHeader } from "../components/util/StyledComponents";
import OpenOrdersSummary from "../componentsCore/cards/OpenOrdersSummary";
import WithManufacturer from "../contexts/WithManufacturer";
import Path from "../routes/Path";
import { RootStore } from "../stores/rootStore";
import withRoot from "../withRoot";

const addDefaultSrc = (ev: React.SyntheticEvent<HTMLImageElement>) => {
  ev.currentTarget.src = "https://api.randmar.io/Images/Logo.svg";
};

const Logo: React.FC<{
  reseller?: { name?: string | null; resellerId?: string | null };
}> = ({ reseller }) => {
  const theme = useTheme();

  return (
    <img
      className="transition-transform duration-200 hover:-translate-y-0.5"
      style={{
        width: "100%",
        height: "110px",
        objectFit: "contain",
        borderRadius: theme.shape.borderRadius,
        backgroundColor: alpha(theme.palette.primary.light, 0.08),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
        padding: "0.5rem",
      }}
      alt={reseller?.name ? `${reseller.name} logo` : "Reseller logo"}
      height="140"
      loading="lazy"
      decoding="async"
      src={`https://api.randmar.io/V4/Partner/${reseller?.resellerId}/Account/Logo`}
      onError={addDefaultSrc}
    />
  );
};

interface IProps {
  store?: RootStore;
  applicationId: string;
}

const GeneralDocument: React.FC<IProps> = ({ store, applicationId }) => {
  const [loading, setLoading] = React.useState(true);
  const { orderNumber } = useParams<{ orderNumber: string }>();
  const theme = useTheme();

  const designTokens = React.useMemo(() => ({
    cardRadius: theme.shape.borderRadius * 1.5,
    borderSubtle: alpha(theme.palette.primary.main, 0.14),
    softHover: alpha(theme.palette.primary.light, 0.16),
    softBackground: alpha(theme.palette.primary.light, 0.08),
    focusRing: alpha(theme.palette.primary.main, 0.4),
    surface: theme.palette.background.paper,
    transition: "160ms ease",
    muted: theme.palette.text.secondary,
  }), [theme]);

  const pageHeadingId = React.useId();
  const orderDetailsHeadingId = React.useId();
  const tableHeadingId = React.useId();
  const resellerHeadingId = React.useId();

  React.useEffect(() => {
    const fetchDocument = async () => {
      const { documentsStore } = store!;
      documentsStore.generalDocument = undefined;
      setLoading(true);

      const docOrderNumber = (orderNumber || "").toUpperCase();
      await documentsStore.getGeneralDocument(applicationId, docOrderNumber);

      setLoading(false);
    }
    fetchDocument();
  }, [applicationId, orderNumber, store]);

  const renderLoading = () => (
    <Stack spacing={2} role="status" aria-live="polite" sx={{ py: 4 }}>
      <LinearProgress color="primary" />
      <Typography variant="body2" color="text.secondary">
        Fetching the latest order details…
      </Typography>
    </Stack>
  );

  const renderError = () => (
    <Alert severity="error" role="alert" sx={{ my: 4 }}>
      We couldn’t load this document. Please try again or refresh the page.
    </Alert>
  );

  const { documentsStore, warehousesStore } = store!;
  const { generalDocument } = documentsStore;

  if (generalDocument === null)
    return renderError();

  if (loading || !generalDocument)
    return renderLoading();

  const businessAddress = (() => {
    const addressParts = [
      generalDocument.reseller?.address1,
      generalDocument.reseller?.address2,
      [generalDocument.reseller?.city, generalDocument.reseller?.province].filter(Boolean).join(", "),
      generalDocument.reseller?.postalCode,
    ].filter(Boolean);

    return addressParts.length > 0 ? addressParts.join(", ") : "No address on file";
  })();

  const activeOrderDetails = generalDocument.activeOrderDetails || [];
  const completedOrderDetails = generalDocument.completedSalesOrderDetails || [];
  const hasOrderDetails = activeOrderDetails.length > 0 || completedOrderDetails.length > 0;
  const canViewProfile = Boolean(generalDocument.reseller?.resellerId);
  const orderDateLabel = (() => {
    if (!generalDocument.orderDate)
      return undefined;

    const parsed = new Date(generalDocument.orderDate);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed.toLocaleDateString();
  })();

  const renderOrderRows = (orderDetails?: typeof generalDocument.activeOrderDetails) =>
    (orderDetails || [])
      .slice()
      .sort((a, b) => (a.mpn || "").localeCompare(b.mpn || ""))
      .map((item) => (
        <TableRow
          key={`${item.randmarSKU}-${item.warehouseCode}-${item.quantity}`}
          hover
          sx={{
            backgroundColor: item.getColor(),
            transition: `background-color ${designTokens.transition}, transform ${designTokens.transition}`,
            "&:hover": {
              backgroundColor: designTokens.softHover,
            },
            "&:focus-within": {
              outline: `2px solid ${designTokens.focusRing}`,
              outlineOffset: -2,
            },
          }}
          aria-label={`Part ${item.mpn || item.randmarSKU}, quantity ${item.quantity}`}
        >
          <TableCell component="th" scope="row" align="left">
            <Box
              component={RouterLink}
              to={Path.product(applicationId, item.randmarSKU)}
              sx={{
                color: theme.palette.primary.main,
                fontWeight: 600,
                textDecoration: "none",
                display: "inline-flex",
                flexDirection: "column",
                gap: 0.25,
                transition: `color ${designTokens.transition}`,
                "&:hover": { color: theme.palette.primary.dark },
                "&:focus-visible": {
                  outline: `2px solid ${designTokens.focusRing}`,
                  outlineOffset: 2,
                  borderRadius: 6,
                },
              }}
            >
              {item.mpn}
              <Typography component="span" variant="caption" color="text.secondary">
                SKU {item.randmarSKU}
              </Typography>
            </Box>
          </TableCell>
          <TableCell align="center">
            <Typography variant="body2" component="span">
              {item.quantity}
            </Typography>
          </TableCell>
          <TableCell align="center">
            <Stack spacing={0.5} alignItems="center">
              <Typography variant="body2">{item.unitWeight || 0} lbs</Typography>
              {item.quantity !== 1 && (
                <Typography variant="subtitle2" fontWeight={600}>
                  {item.extendedWeight || 0} lbs
                </Typography>
              )}
            </Stack>
          </TableCell>
          <TableCell align="center">
            <Stack spacing={0.5} alignItems="center">
              <FormattedNumber value={item.unitPrice || 0} {...CurrencyUtils.format} />
              {item.quantity !== 1 && (
                <Typography variant="subtitle2" fontWeight={600}>
                  <FormattedNumber value={item.extendedPrice || 0} {...CurrencyUtils.format} />
                </Typography>
              )}
            </Stack>
          </TableCell>
          <TableCell align="center">
            {warehousesStore.getWarehouseNameFromCode(item.warehouseCode) || "TBD"}
          </TableCell>
        </TableRow>
      ));

  return (
    <Box component="main" aria-labelledby={pageHeadingId} sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box component="header" role="banner">
        <DocumentHeader justifyContent="space-between" alignItems="center" direction="row">
          <Stack spacing={1} direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }}>
            <Typography variant="h1" id={pageHeadingId}>
              General Document
            </Typography>
            <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" } }} />
            <Typography variant="h2" component="p">
              {generalDocument.orderNumber}
            </Typography>
          </Stack>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems="flex-end">
            {orderDateLabel && (
              <Chip size="small" label={`Ordered ${orderDateLabel}`} />
            )}
            {generalDocument.allowPartialShipment !== undefined && (
              <Chip
                size="small"
                color={generalDocument.allowPartialShipment ? "success" : "default"}
                label={generalDocument.allowPartialShipment ? "Partial shipment allowed" : "Full shipment"}
              />
            )}
          </Stack>
        </DocumentHeader>
      </Box>

      <Stack direction={{ xs: "column", lg: "row" }} spacing={{ xs: 3, lg: 4 }} alignItems="flex-start">
        <Stack component="section" spacing={3} flex={2} className="w-full">
          <Accordion type="single" collapsible defaultValue="item-1" className="w-full">
            <AccordionItem value="item-1">
              <AccordionTrigger aria-controls="general-document-summary" id={orderDetailsHeadingId}>
                Order details
              </AccordionTrigger>
              <AccordionContent id="general-document-summary" role="region" aria-labelledby={orderDetailsHeadingId}>
                <GeneralDocumentSummary generalDocument={generalDocument} />
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {activeOrderDetails.length > 0 && (
            <Box
              component="section"
              aria-label="Open orders summary"
              sx={{
                borderRadius: designTokens.cardRadius,
                border: `1px solid ${designTokens.borderSubtle}`,
                backgroundColor: designTokens.surface,
                boxShadow: "0 10px 40px rgba(15, 23, 42, 0.06)",
                transition: `transform ${designTokens.transition}, box-shadow ${designTokens.transition}`,
                "&:hover": { transform: "translateY(-2px)" },
              }}
            >
              <OpenOrdersSummary openOrders={activeOrderDetails} />
            </Box>
          )}

          <Box component="section" aria-labelledby={tableHeadingId}>
            <Typography variant="h3" id={tableHeadingId} gutterBottom>
              Line items
            </Typography>
            <TableContainer
              sx={{
                borderRadius: designTokens.cardRadius,
                border: `1px solid ${designTokens.borderSubtle}`,
                backgroundColor: designTokens.surface,
                boxShadow: "0 15px 35px rgba(15, 23, 42, 0.08)",
              }}
            >
              <Table aria-labelledby={tableHeadingId} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell component="th" scope="col" align="left">
                      Part number
                    </TableCell>
                    <TableCell component="th" scope="col">
                      Qty
                    </TableCell>
                    <TableCell component="th" scope="col">
                      Weight
                    </TableCell>
                    <TableCell component="th" scope="col">
                      Price
                    </TableCell>
                    <TableCell component="th" scope="col">
                      Location
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {hasOrderDetails ? (
                    <>
                      {renderOrderRows(activeOrderDetails)}
                      {renderOrderRows(completedOrderDetails)}
                    </>
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="text.secondary">
                          There are no order lines yet. Check back once the order is confirmed.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Stack>

        <Card
          className="w-full lg:max-w-sm transition-transform duration-200 hover:-translate-y-0.5 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-[#582c83]/30"
          aria-labelledby={resellerHeadingId}
          style={{
            borderRadius: designTokens.cardRadius,
            border: `1px solid ${designTokens.borderSubtle}`,
            backgroundColor: designTokens.surface,
            boxShadow: "0 15px 35px rgba(15, 23, 42, 0.08)",
            transition: `transform ${designTokens.transition}, box-shadow ${designTokens.transition}`,
          }}
        >
          <CardHeader>
            <CardTitle id={resellerHeadingId}>{generalDocument.reseller?.name || "Reseller"}</CardTitle>
            <CardDescription>Reseller overview</CardDescription>
          </CardHeader>
          <Stack spacing={3} padding={{ xs: 2, md: 3 }}>
            <Logo reseller={generalDocument.reseller} />
            {generalDocument.reseller?.priority && (
              <Chip
                aria-label={`Priority ${generalDocument.reseller.priority}`}
                size="small"
                variant="outlined"
                color="info"
                label={generalDocument.reseller.priority}
                sx={{ alignSelf: "center" }}
              />
            )}
            <Box
              component="dl"
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
                padding: 2,
                borderRadius: designTokens.cardRadius,
                border: `1px dashed ${designTokens.borderSubtle}`,
                backgroundColor: designTokens.softBackground,
              }}
            >
              <Typography component="dt" variant="subtitle2" color={designTokens.muted}>
                Main representative
              </Typography>
              <Typography component="dd" fontWeight={600}>
                {generalDocument.reseller?.contactName || "Not listed"}
              </Typography>
              <Typography component="dt" variant="subtitle2" color={designTokens.muted}>
                Business address
              </Typography>
              <Typography component="dd" fontWeight={600}>
                {businessAddress}
              </Typography>
              <Typography component="dt" variant="subtitle2" color={designTokens.muted}>
                Email
              </Typography>
              <Typography component="dd" fontWeight={600}>
                {generalDocument.reseller?.publicEmail || "Not shared"}
              </Typography>
              <Typography component="dt" variant="subtitle2" color={designTokens.muted}>
                Phone
              </Typography>
              <Typography component="dd" fontWeight={600}>
                {generalDocument.reseller?.phone || "Not shared"}
              </Typography>
            </Box>
            {canViewProfile ? (
              <Button
                asChild
                className="w-full focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#582c83]"
              >
                <RouterLink to={Path.reseller(applicationId, generalDocument.reseller?.resellerId)} aria-label="View full reseller profile" className="block w-full">
                  View complete profile
                </RouterLink>
              </Button>
            ) : (
              <Button className="w-full" disabled aria-disabled="true">
                Profile unavailable
              </Button>
            )}
          </Stack>
        </Card>
      </Stack>
    </Box>
  );
}

export default withRoot(WithManufacturer(inject("store")(observer(GeneralDocument))));
